import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import Button from "../Button";
import Typography from "../Typography";
import { getThemeStyleFromAnchor } from "../DatePicker/datePickerPortalTheme";
import {
  formatStoredTime,
  getCurrentTime,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  padTimeUnit,
  parseStoredTime,
} from "./timePickerValue";
import "./TimePickerPopover.scss";

const DROPDOWN_GAP = 8;
const PANEL_WIDTH = 200;

function getDefaultPendingTime(value, defaultToCurrentTime = false) {
  const parsed = parseStoredTime(value);
  if (parsed.isValid) {
    return { hour: parsed.hour, minute: parsed.minute };
  }

  if (defaultToCurrentTime) {
    return getCurrentTime();
  }

  return { hour: null, minute: null };
}

function TimeColumn({ label, options, selectedValue, onSelect, formatOption }) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  useLayoutEffect(() => {
    if (selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({ block: "center" });
    }
  }, [selectedValue]);

  return (
    <div className="ds-time-picker__column">
      <Typography
        variant="bodySmall"
        component="p"
        className="ds-time-picker__column-label"
      >
        {label}
      </Typography>
      <div
        ref={listRef}
        className="ds-time-picker__column-list"
        role="listbox"
        aria-label={label}
      >
        {options.map((option) => {
          const isSelected = selectedValue === option;
          return (
            <Button
              key={option}
              ref={isSelected ? selectedRef : null}
              variant="tertiary"
              size="sm"
              role="option"
              aria-selected={isSelected}
              className={[
                "ds-time-picker__option",
                isSelected && "ds-time-picker__option--selected",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onSelect(option)}
            >
              <Typography variant="bodyMedium" component="span">
                {formatOption(option)}
              </Typography>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

TimeColumn.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.number).isRequired,
  selectedValue: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  formatOption: PropTypes.func.isRequired,
};

TimeColumn.defaultProps = {
  selectedValue: undefined,
};

export default function TimePickerPopover({
  open,
  onDismiss,
  onApply,
  onClear,
  value,
  defaultToCurrentTime = false,
  anchorRef,
  overlayClassName = "",
}) {
  const intl = useIntl();
  const panelRef = useRef(null);
  const initialPending = getDefaultPendingTime(value, defaultToCurrentTime);

  const [pendingHour, setPendingHour] = useState(() => initialPending.hour);
  const [pendingMinute, setPendingMinute] = useState(
    () => initialPending.minute,
  );
  const [panelStyle, setPanelStyle] = useState({ top: 0, left: 0 });
  const [overlayThemeStyle, setOverlayThemeStyle] = useState(undefined);

  useEffect(() => {
    if (!open) return;
    const next = getDefaultPendingTime(value, defaultToCurrentTime);
    setPendingHour(next.hour);
    setPendingMinute(next.minute);
  }, [defaultToCurrentTime, open, value]);

  const updatePanelPosition = useCallback(() => {
    const el = anchorRef?.current;
    if (!el) return;
    const {
      top: anchorTop,
      left: anchorLeft,
      bottom: anchorBottom,
    } = el.getBoundingClientRect();
    let top = anchorBottom + DROPDOWN_GAP;
    let left = anchorLeft;

    if (panelRef.current) {
      const estH = panelRef.current.offsetHeight || 320;
      if (
        top + estH > window.innerHeight - 12 &&
        anchorTop > estH + DROPDOWN_GAP
      ) {
        top = anchorTop - estH - DROPDOWN_GAP;
      }
    }

    const maxLeft = window.innerWidth - PANEL_WIDTH - 12;
    if (left > maxLeft) left = Math.max(12, maxLeft);
    if (left < 12) left = 12;

    setPanelStyle({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    setOverlayThemeStyle(getThemeStyleFromAnchor(anchorRef?.current));
    updatePanelPosition();
    const onScroll = () => updatePanelPosition();
    const onResize = () => updatePanelPosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, updatePanelPosition, pendingHour, pendingMinute]);

  useEffect(() => {
    if (!open || !onDismiss) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onDismiss();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onDismiss]);

  const canApply = pendingHour != null && pendingMinute != null;

  const handleDone = useCallback(() => {
    if (!canApply) return;
    onApply(formatStoredTime(pendingHour, pendingMinute));
    onDismiss();
  }, [canApply, onApply, onDismiss, pendingHour, pendingMinute]);

  const hasStoredValue = parseStoredTime(value).isValid;

  const handleClear = useCallback(
    (event) => {
      event?.preventDefault?.();
      onClear?.(event);
      onDismiss();
    },
    [onClear, onDismiss],
  );

  if (!open) return null;

  const overlayClass = ["ds-time-picker__overlay", overlayClassName]
    .filter(Boolean)
    .join(" ");

  return ReactDOM.createPortal(
    <div className={overlayClass} style={overlayThemeStyle}>
      <button
        type="button"
        className="ds-time-picker__backdrop"
        aria-label={intl.formatMessage({
          id: "close",
          defaultMessage: "Close",
        })}
        onMouseDown={(event) => {
          event.preventDefault();
          onDismiss();
        }}
      />
      <div
        ref={panelRef}
        className="ds-time-picker"
        style={{
          position: "fixed",
          top: panelStyle.top,
          left: panelStyle.left,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({
          id: "select-time",
          defaultMessage: "Select Time",
        })}
      >
        <div className="ds-time-picker__columns">
          <TimeColumn
            label={intl.formatMessage({
              id: "ds-time-picker-hour",
              defaultMessage: "Hour",
            })}
            options={HOUR_OPTIONS}
            selectedValue={pendingHour}
            onSelect={setPendingHour}
            formatOption={padTimeUnit}
          />
          <TimeColumn
            label={intl.formatMessage({
              id: "ds-time-picker-minute",
              defaultMessage: "Minute",
            })}
            options={MINUTE_OPTIONS}
            selectedValue={pendingMinute}
            onSelect={setPendingMinute}
            formatOption={padTimeUnit}
          />
        </div>
        <div className="ds-time-picker__footer">
          {hasStoredValue && onClear ? (
            <Button
              variant="link"
              size="sm"
              className="ds-time-picker__clear-btn"
              onClick={handleClear}
            >
              {intl.formatMessage({
                id: "clear",
                defaultMessage: "Clear",
              })}
            </Button>
          ) : (
            <Typography
              variant="bodySmall"
              component="p"
              className="ds-time-picker__hint"
            >
              {intl.formatMessage({
                id: "ds-time-picker-hint",
                defaultMessage: "Select a time",
              })}
            </Typography>
          )}
          <Button
            variant="primary"
            size="sm"
            className="ds-time-picker__done-btn"
            disabled={!canApply}
            onClick={handleDone}
          >
            {intl.formatMessage({
              id: "ds-time-picker-done",
              defaultMessage: "Done",
            })}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

TimePickerPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  value: PropTypes.string,
  defaultToCurrentTime: PropTypes.bool,
  anchorRef: PropTypes.shape({
    current: PropTypes.oneOfType([
      PropTypes.instanceOf(
        typeof HTMLElement !== "undefined" ? HTMLElement : Object,
      ),
      PropTypes.oneOf([null]),
    ]),
  }),
  overlayClassName: PropTypes.string,
};

TimePickerPopover.defaultProps = {
  onClear: undefined,
  value: "",
  defaultToCurrentTime: false,
  anchorRef: null,
  overlayClassName: "",
};
